import { useState } from 'react';
import { ProcessingIcon, CheckIcon, CopyIcon } from '../../../icons';

export interface InlineInputProps {
  /** Input value */
  value: string;
  /** Input name attribute */
  name?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Accessible label */
  ariaLabel?: string;
  /** Make input read-only */
  readOnly?: boolean;
  /** Disable input interactions */
  disabled?: boolean;
  /** Mark input as required */
  required?: boolean;
  /** Show loading spinner */
  isLoading?: boolean;
  /** Show success state icon */
  isSuccess?: boolean;
  /** Show error state icon */
  isError?: boolean;
  /** Optional error message to display */
  errorMessage?: string;
  /** Value change callback */
  onChange: (value: string) => void;
  /** Edit callback (e.g., on Enter key press) */
  onEdit: () => void | Promise<void>;
  /** Show a copy button that copies the field value to the clipboard */
  copyable?: boolean;
  /** Optional wrapper class */
  className?: string;
}

export const InlineInput = ({
  value,
  name = 'inline-input',
  placeholder = 'Enter value',
  ariaLabel = 'Inline input',
  readOnly = false,
  disabled = false,
  required = true,
  isLoading = false,
  isSuccess = false,
  isError = false,
  errorMessage = '',
  onChange,
  onEdit,
  copyable = false,
  className = '',
}: InlineInputProps) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);
      alert('Copied to clipboard');
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // clipboard access denied — silently ignore
    }
  };

  return (
    <div className={`field ${className} ${copyable ? 'copyable' : ''}`}>
      <div className={`input-wrapper ${isLoading || isSuccess || copyable ? 'icon-right' : ''}`}>
        <input
          type="text"
          name={name}
          readOnly={readOnly}
          disabled={disabled}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onEdit();
            }
          }}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className={`${isError ? 'invalid' : ''}`}
        />
        {isLoading && (
            <ProcessingIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" style={{ right: '8px' }} className='spinner' />
        )}
        {isSuccess && !isLoading && (
          <CheckIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" style={{ right: '8px' }} className="success-icon"  />
        )}
        {copyable && !isLoading && !isSuccess && (
          <button
            type="button"
            className="btn btn-ghost copy-btn"
            aria-label="Copy to clipboard"
            title={isCopied ? 'Copied!' : 'Copy to clipboard'}
            onClick={handleCopy}
          >
            <CopyIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" style={{ right: '8px' }} />
          </button>
        )}
      </div>
      {isError && errorMessage && <p className="error-message">{errorMessage}</p>}
    </div>
  );
};
